import React, { useState, useMemo } from "react";
import { format, parseISO, addMonths, subMonths, subDays } from "date-fns";
import { IStudentPlan } from "../../../state";
import style from "./StudentHistoryList.module.scss";

export interface IStudentHistoryListProps {
  studentPlans: IStudentPlan[];
  title: string;
}

export const StudentHistoryList = ({
  studentPlans,
  title,
}: IStudentHistoryListProps) => {
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(
    format(subDays(addMonths(new Date(), 1), 1), "yyyy-MM-dd"),
  );

  const handleDateChange = (isStartDate: boolean, newValue: string) => {
    const newDate = parseISO(newValue);
    if (isStartDate) {
      setStartDate(format(newDate, "yyyy-MM-dd"));
      const newEndDate = subDays(addMonths(newDate, 1), 1);
      setEndDate(format(newEndDate, "yyyy-MM-dd"));
    } else {
      const adjustedEndDate = subDays(newDate, 1);
      setEndDate(format(adjustedEndDate, "yyyy-MM-dd"));
      setStartDate(format(subMonths(newDate, 1), "yyyy-MM-dd"));
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleDateChange(true, e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleDateChange(false, e.target.value);
  };

  const filterStudentPlans = useMemo((): IStudentPlan[] => {
    const selectedStartAt = parseISO(startDate);
    const selectedEndAt = parseISO(endDate);
    return studentPlans.filter(studentPlan => {
      const planStartAt = parseISO(studentPlan.start_at);
      const planEndAt = studentPlan.end_at
        ? parseISO(studentPlan.end_at)
        : selectedEndAt;
      return planStartAt <= selectedEndAt && planEndAt >= selectedStartAt;
    });
  }, [studentPlans, startDate, endDate]);

  return (
    <div className={style.list}>
      <div className="d-flex align-items-center mb-4">
        <h3 className="me-4 mb-0">{title}</h3>
        <div className={style.searchArea}>
          <label className="me-2">利用期間:</label>
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
          />
          <span className="mx-2">-</span>
          <input type="date" value={endDate} onChange={handleEndDateChange} />
        </div>
      </div>
      <table className="table table-striped table-hover">
        <thead>
          <tr>
            <th>名前</th>
            <th>プラン</th>
          </tr>
        </thead>
        <tbody>
          {filterStudentPlans.map(studentPlan => (
            <tr
              key={studentPlan.id}
              className={studentPlan.plan_disabled ? style.planDisabled : ""}
            >
              <td>
                {studentPlan.student_name}
                {studentPlan.plan_disabled ? "（退塾処理済み）" : ""}
              </td>
              <td>
                <div>{studentPlan.name}</div>
                {format(parseISO(studentPlan.start_at), "yyyy/MM/dd")}
                {studentPlan.end_at &&
                  ` - ${format(parseISO(studentPlan.end_at), "yyyy/MM/dd")}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StudentHistoryList;
